import {useQuery} from '@aboutyou/data-fetcher';
import {useDefaultCookieDomain} from '@aboutyou/storage';
import {localStorageRemoveItem, removeCookie} from '@aboutyou/utils';
import {USER_CACHE_KEY} from '../../CacheKeys';
import {
  getUserDataFromCookies,
  setUserDataToCookies,
} from '../../utils/read-write-user-data';
import {BroadcastUserDataToTabs} from '../SyncSessionBetweenTabs';
import {createDataStore} from '../createDataStore';
import {TADARIDA_USER_PERSIST_KEY} from './constants';
import {UserState} from './types';

/**
 * One-time cleanup: the login migration stopped using `tadarida-optional-user-v1`, but users
 * who had it stored still carry the stale customer data in local storage.
 * NOTE: This can be removed 3 months after the login migration (ASFE-14626) is released.
 * https://aboutyou.atlassian.net/browse/ASFE-14721
 */
if (process.browser) {
  localStorageRemoveItem('tadarida-optional-user-v1');
}

export const cleanupUserPersistedState = ({
  cookieDomain,
}: {
  cookieDomain?: string;
}) => removeCookie({name: TADARIDA_USER_PERSIST_KEY, domain: cookieDomain});

const useExpensiveUser = () => {
  const cookieDomain = useDefaultCookieDomain();

  return useQuery(
    USER_CACHE_KEY,
    async (): Promise<UserState | null> =>
      getUserDataFromCookies<UserState>({
        storageKey: TADARIDA_USER_PERSIST_KEY,
        cookieDomain,
      }),
    {
      onSuccess: (userState: UserState | null) =>
        onUserSuccess({userState, cookieDomain}),
    },
  );
};

const onUserSuccess = ({
  userState,
  cookieDomain,
}: {
  userState: UserState | null;
  cookieDomain?: string;
}) => {
  BroadcastUserDataToTabs.broadcastData({
    key: TADARIDA_USER_PERSIST_KEY,
    data: userState,
  });

  if (userState?.token) {
    setUserDataToCookies({
      storageKey: TADARIDA_USER_PERSIST_KEY,
      data: userState,
      cookieDomain,
    });
  }
};

const {Provider, useData: useUser} = createDataStore<UserState | null>({
  defaultValue: null,
  useQueryHook: useExpensiveUser,
});

export {Provider as UserProvider, useUser};
